import { listCached } from '../cache/db'

const COLLECTIONS = [
  { key: 'app.bsky.feed.post', short: 'post' },
  { key: 'app.bsky.feed.repost', short: 'repost' },
  { key: 'app.bsky.feed.like', short: 'like' },
  { key: 'app.bsky.graph.follow', short: 'follow' },
  { key: 'app.bsky.graph.block', short: 'block' },
] as const

type CollShort = (typeof COLLECTIONS)[number]['short']

interface ExportedPost {
  kind: 'post' | 'reply'
  uri: string
  createdAt: string
  text: string
  tags: string[]
  alts: string[]
  /** Present only when this is a reply — the parent post's uri. */
  replyParentUri?: string
  imageCount: number
}

interface ExportedSubjectRef {
  kind: 'repost' | 'like' | 'follow' | 'block'
  /** Always normalised to a string (uri for repost/like, DID for follow/block). */
  subject: string
  createdAt: string
}

export interface ExportedTab {
  handle: string
  posts: ExportedPost[]
  replies: ExportedPost[]
  reposts: ExportedSubjectRef[]
  likes: ExportedSubjectRef[]
  follows: ExportedSubjectRef[]
  blocks: ExportedSubjectRef[]
  counts: Record<CollShort, number>
}

export interface CacheExport {
  exportedAt: string
  daysBack: number
  cutoffIso: string
  totals: Record<CollShort, number> & { tabs: number }
  tabs: ExportedTab[]
}

const HASHTAG_RE = /#([\p{L}\p{N}_]+)/gu

function extractTags(text: string, facets: unknown): string[] {
  const out = new Set<string>()
  for (const m of text.matchAll(HASHTAG_RE)) {
    out.add(m[1].toLowerCase())
  }
  // Some posts surface tags via facets only — pick those up too.
  if (Array.isArray(facets)) {
    for (const f of facets as Array<Record<string, unknown>>) {
      const features = (f as { features?: unknown }).features
      if (!Array.isArray(features)) continue
      for (const feat of features as Array<Record<string, unknown>>) {
        if (feat.$type === 'app.bsky.richtext.facet#tag' && typeof feat.tag === 'string') {
          out.add(feat.tag.toLowerCase())
        }
      }
    }
  }
  return Array.from(out)
}

function extractAlts(embed: unknown): { alts: string[]; imageCount: number } {
  const alts: string[] = []
  let imageCount = 0
  if (!embed || typeof embed !== 'object') return { alts, imageCount }
  const e = embed as Record<string, unknown>
  // direct images embed
  const directImages = e.images as Array<{ alt?: string }> | undefined
  if (Array.isArray(directImages)) {
    imageCount += directImages.length
    for (const i of directImages) {
      if (typeof i.alt === 'string' && i.alt.trim()) alts.push(i.alt.trim())
    }
  }
  // recordWithMedia → media.images
  const media = e.media as Record<string, unknown> | undefined
  if (media && Array.isArray((media as { images?: unknown[] }).images)) {
    const imgs = media.images as Array<{ alt?: string }>
    imageCount += imgs.length
    for (const i of imgs) {
      if (typeof i.alt === 'string' && i.alt.trim()) alts.push(i.alt.trim())
    }
  }
  // external link card metadata — title + description are descriptive
  // text similar to alts, harmless to include.
  const ext = e.external as { title?: string; description?: string } | undefined
  if (ext) {
    if (typeof ext.title === 'string' && ext.title.trim()) alts.push(`[link title] ${ext.title.trim()}`)
    if (typeof ext.description === 'string' && ext.description.trim())
      alts.push(`[link desc] ${ext.description.trim()}`)
  }
  return { alts, imageCount }
}

function subjectString(value: Record<string, unknown>): string {
  const subj = value.subject
  if (typeof subj === 'string') return subj
  if (subj && typeof subj === 'object' && 'uri' in subj && typeof (subj as { uri?: unknown }).uri === 'string') {
    return (subj as { uri: string }).uri
  }
  return ''
}

/** Build a serializable JSON snapshot of every open tab's cached
 *  records inside the last `daysBack` window. Images are intentionally
 *  represented by their alt-text and tag set — no image bytes or URLs
 *  are included so the export stays small and shareable. */
export async function buildCacheExport(
  tabs: { handle: string }[],
  daysBack: number,
): Promise<CacheExport> {
  const now = Date.now()
  const cutoff = now - daysBack * 24 * 60 * 60 * 1000
  const exportedTabs: ExportedTab[] = []
  const grandTotals: Record<CollShort, number> = {
    post: 0,
    repost: 0,
    like: 0,
    follow: 0,
    block: 0,
  }

  for (const t of tabs) {
    const tab: ExportedTab = {
      handle: t.handle,
      posts: [],
      replies: [],
      reposts: [],
      likes: [],
      follows: [],
      blocks: [],
      counts: { post: 0, repost: 0, like: 0, follow: 0, block: 0 },
    }
    for (const coll of COLLECTIONS) {
      let rows
      try {
        rows = await listCached(t.handle.toLowerCase(), coll.key, 0, 5000)
      } catch {
        continue
      }
      for (const r of rows) {
        const createdAt =
          (r.value.createdAt as string | undefined) ??
          new Date(r.fetched_at).toISOString()
        const ts = Date.parse(createdAt)
        if (Number.isNaN(ts) || ts < cutoff) continue
        if (coll.key === 'app.bsky.feed.post') {
          const text = typeof r.value.text === 'string' ? r.value.text : ''
          const reply = r.value.reply as
            | { parent?: { uri?: string } }
            | undefined
          const isReply = !!reply?.parent?.uri
          const { alts, imageCount } = extractAlts(r.value.embed)
          const tags = extractTags(text, r.value.facets)
          const entry: ExportedPost = {
            kind: isReply ? 'reply' : 'post',
            uri: r.uri,
            createdAt,
            text,
            tags,
            alts,
            imageCount,
            replyParentUri: isReply ? reply!.parent!.uri : undefined,
          }
          if (isReply) tab.replies.push(entry)
          else tab.posts.push(entry)
          tab.counts.post++
          grandTotals.post++
        } else {
          const ref: ExportedSubjectRef = {
            kind: coll.short as 'repost' | 'like' | 'follow' | 'block',
            subject: subjectString(r.value),
            createdAt,
          }
          if (coll.short === 'repost') {
            tab.reposts.push(ref)
            tab.counts.repost++
            grandTotals.repost++
          } else if (coll.short === 'like') {
            tab.likes.push(ref)
            tab.counts.like++
            grandTotals.like++
          } else if (coll.short === 'follow') {
            tab.follows.push(ref)
            tab.counts.follow++
            grandTotals.follow++
          } else if (coll.short === 'block') {
            tab.blocks.push(ref)
            tab.counts.block++
            grandTotals.block++
          }
        }
      }
    }
    exportedTabs.push(tab)
  }

  return {
    exportedAt: new Date(now).toISOString(),
    daysBack,
    cutoffIso: new Date(cutoff).toISOString(),
    totals: { ...grandTotals, tabs: exportedTabs.length },
    tabs: exportedTabs,
  }
}
